/**
 * The three small charts of a team's or an agent's Overview, drawn by hand so they take the console's tokens in light
 * and dark: when it works (a weekday by hour heatmap on one hue), how fast its error budget burns (a step line against
 * an even pace, with each failed run marked), and how long each thing has waited on a person (bars of age). Colour is
 * never the only signal: every cell, point and bar has its number in words on hover and for a screen reader.
 * Styles in overview.css.
 */

import type { MemberOverview } from '@aioe/shared';
import { Link } from 'react-router-dom';

import { ageWords, busiest, cellWords, heatStep, hourLabel, percent, plural, WAITING_KIND_WORDS, waitingLink, WEEKDAYS } from './overviewModel.ts';

/** Model calls by weekday and hour, Monday first, in the organisation's time zone. */
export function Heatmap({ heatmap, timeZone }: { heatmap: MemberOverview['heatmap']; timeZone: string }) {
  const top = busiest(heatmap, 3);
  const summary = top.length ? `Busiest: ${top.map((c) => `${cellWords(c.day, c.hour)}, ${plural(c.calls, 'call', 'calls')}`).join('; ')}` : 'No model calls in this range';
  return (
    <figure className="ov-heat" aria-label={`Model calls by weekday and hour (${timeZone}). ${summary}.`}>
      <div className="ov-heat__grid" role="presentation">
        {heatmap.cells.map((row, day) => (
          <div key={WEEKDAYS[day]} className="ov-heat__row">
            <span className="ov-heat__day" aria-hidden="true">{WEEKDAYS[day]}</span>
            {row.map((n, hour) => (
              <span key={hour} className="ov-heat__cell" data-step={heatStep(n, heatmap.max)} title={`${cellWords(day, hour)}: ${plural(n, 'call', 'calls')}`} />
            ))}
          </div>
        ))}
        <div className="ov-heat__row ov-heat__axis" aria-hidden="true">
          <span className="ov-heat__day" />
          {Array.from({ length: 24 }, (_, h) => (
            <span key={h} className="ov-heat__hour">{h % 3 === 0 ? hourLabel(h) : ''}</span>
          ))}
        </div>
      </div>
      <figcaption className="sr-only">{summary}.</figcaption>
    </figure>
  );
}

/** The heatmap's legend: Fewer, the steps, More. */
export function HeatLegend() {
  return (
    <span className="ov-heat__legend" aria-hidden="true">
      Fewer
      {[1, 2, 3, 4].map((s) => <span key={s} className="ov-heat__cell ov-heat__swatch" data-step={s} />)}
      More
    </span>
  );
}

const shortDay = (day: string) => new Date(`${day}T12:00:00Z`).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', timeZone: 'UTC' });

/** The error budget left at the end of each day of the window, against spending it evenly, with each failed run. */
export function BurnChart({ run }: { run: MemberOverview['runSuccess'] }) {
  const days = run.series;
  if (days.length < 2) return null;
  const W = 300;
  const H = 100;
  const x = (i: number) => (i / (days.length - 1)) * W;
  const y = (left: number) => H - left * H;
  // A step line: flat through a day, down at its end when a failure spent some budget.
  let d = `M0,${y(1)}`;
  let prev = 1;
  days.forEach((p, i) => {
    if (p.budgetLeft !== prev) d += ` L${x(i)},${y(prev)} L${x(i)},${y(p.budgetLeft)}`;
    prev = p.budgetLeft;
  });
  d += ` L${W},${y(prev)}`;
  const index = new Map(days.map((p, i) => [p.day, i] as const));
  const marks = run.failures
    .map((f) => ({ ...f, i: index.get(f.day) ?? days.length - 1 }))
    .slice(-40);
  const labelAt = [0, Math.round((days.length - 1) / 2), days.length - 1];
  return (
    <figure className="ov-burn" aria-label={`Error budget left over the last ${days.length} days: ${percent(run.budgetLeft)} now, ${plural(run.failed, 'failed run', 'failed runs')}.`}>
      <div className="ov-burn__legend" aria-hidden="true">
        <span><i className="ov-burn__key ov-burn__key--left" />Budget left</span>
        <span><i className="ov-burn__key ov-burn__key--pace" />Even pace</span>
        <span><i className="ov-burn__key ov-burn__key--fail" />Failed run</span>
      </div>
      <div className="ov-burn__plot">
        <div className="ov-burn__y" aria-hidden="true">
          {[100, 75, 50, 25, 0].map((v) => <span key={v}>{v}%</span>)}
        </div>
        <div className="ov-burn__area">
          <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="ov-burn__svg" aria-hidden="true">
            {[0, 25, 50, 75, 100].map((v) => <line key={v} x1={0} x2={W} y1={y(v / 100)} y2={y(v / 100)} className="ov-burn__grid" vectorEffect="non-scaling-stroke" />)}
            <line x1={0} y1={y(1)} x2={W} y2={y(0)} className="ov-burn__pace" vectorEffect="non-scaling-stroke" />
            <path d={d} className="ov-burn__line" vectorEffect="non-scaling-stroke" />
          </svg>
          {/* One hover target per day, wider than the line: the day and what was left. */}
          <div className="ov-burn__days">
            {days.map((p) => <span key={p.day} title={`${shortDay(p.day)}: ${percent(p.budgetLeft)} of the error budget left`} />)}
          </div>
          {marks.map((m) => (
            <span key={m.runId} className="ov-burn__fail" style={{ left: `${(m.i / (days.length - 1)) * 100}%`, top: `${(1 - m.budgetLeft) * 100}%` }} title={`A run failed ${new Date(m.at).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })}: ${percent(m.budgetLeft)} left after it`} />
          ))}
        </div>
      </div>
      <div className="ov-burn__x" aria-hidden="true">
        {labelAt.map((i) => <span key={i}>{shortDay(days[i]!.day)}</span>)}
      </div>
    </figure>
  );
}

/** Each thing waiting on a person, as a bar of how long it has waited, oldest first; each opens where it is answered. */
export function AgeBars({ items, now }: { items: MemberOverview['waiting']['items']; now: number }) {
  const ages = items.map((w) => Math.max(0, now - Date.parse(w.since)));
  const max = Math.max(1, ...ages);
  return (
    <ul className="ov-ages">
      {items.map((w, i) => {
        // The title says what it is ("Kiara needs approval: …", "Blocked: …"); the kind is said only for a bare one.
        const title = w.kind === 'other' ? `${WAITING_KIND_WORDS.other}: ${w.title}` : w.title;
        return (
          <li key={w.id}>
            <Link to={waitingLink(w)} className="ov-ages__row" title={`${title}${w.projectName ? ` (${w.projectName})` : ''}, waiting ${ageWords(ages[i]!)}`}>
              <span className="ov-ages__title">{title}</span>
              <span className="ov-ages__track" aria-hidden="true">
                <span className="ov-ages__bar" style={{ width: `${Math.max(3, (ages[i]! / max) * 100)}%` }} />
              </span>
              <span className="ov-ages__age">{ageWords(ages[i]!)}</span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
